import { Controller } from "@hotwired/stimulus"
import { createLogger } from '../utils/logger'

export default class extends Controller {
  static targets = [
    "logsContainer",
    "logEntry",
    "searchInput",
    "filterButton",
    "preserveLogCheckbox",
    "devToolsCheckbox",
    "allSparkCheckbox",
    "sendErrorsButton",
    "errorCountBadge"
  ]

  static values = {
    buildSessionId: String,
    currentFilter: { type: String, default: "all" },
    searchTerm: { type: String, default: "" },
    preserveLog: { type: Boolean, default: false },
    showDevToolsLogs: { type: Boolean, default: false },
    showAllSparkLogs: { type: Boolean, default: true },
    logs: { type: Array, default: [] },
    errorLogs: { type: Array, default: [] }
  }

  connect() {
    this.logger = createLogger('console-log')
    this.logger.debug('Console log controller connected')
    try {
      this.setupEventListeners()
      this.loadStoredLogs()
      // Update badge count on initial load
      setTimeout(() => {
        this.filterLogs()
      }, 100)
    } catch (error) {
      this.logger.error('Console log controller connection error:', error)
    }
  }

  disconnect() {
    this.teardownEventListeners()
  }

  setupEventListeners() {
    this.logger.debug('Setting up console event listeners')
    // Listen for console messages from parent controller
    this.element.addEventListener('preview-devtools:console-message', this.handleConsoleMessage.bind(this))
    this.element.addEventListener('preview-devtools:clear-console', this.clearLogs.bind(this))
    this.element.addEventListener('preview-devtools:export-console', this.exportLogs.bind(this))
    
    // Also listen on document for bubbled events
    document.addEventListener('preview-devtools:console-message', this.handleConsoleMessage.bind(this))
  }

  teardownEventListeners() {
    this.element.removeEventListener('preview-devtools:console-message', this.handleConsoleMessage.bind(this))
    this.element.removeEventListener('preview-devtools:clear-console', this.clearLogs.bind(this))
    this.element.removeEventListener('preview-devtools:export-console', this.exportLogs.bind(this))
  }

  handleConsoleMessage(event) {
    const logData = event.detail
    this.addLog(logData)
  }

  addLog(logData) {
    // Check if this is a DevTools message and if we should filter it
    const isDevToolsMessage = this.isDevToolsLog(logData)
    if (isDevToolsMessage && !this.showDevToolsLogsValue) {
      return // Skip adding this log
    }
    
    // Check if this is an AllSpark message and if we should filter it
    const message = logData.message || ''
    const isAllSparkMessage = message.includes('[allspark]') || message.includes('[AllSpark]')
    if (isAllSparkMessage && !this.showAllSparkLogsValue) {
      return // Skip adding this log
    }
    
    // Add to logs array
    this.logsValue = [...this.logsValue, logData]
    
    // Track errors separately
    if (logData.level === 'error' && !isDevToolsMessage) {
      this.errorLogsValue = [...this.errorLogsValue, logData]
      this.updateErrorCount()
    }
    
    // Create log entry element
    const logEntry = this.createLogEntry(logData)
    
    // Mark as DevTools message if applicable
    if (isDevToolsMessage) {
      logEntry.dataset.devtools = 'true'
    }
    
    // Add to container
    this.logsContainerTarget.appendChild(logEntry)
    
    // Auto-scroll to bottom
    this.logsContainerTarget.scrollTop = this.logsContainerTarget.scrollHeight
    
    // Store if preserve log is enabled
    if (this.preserveLogValue) {
      this.storeLog(logData)
    }
    
    // Update the badge count after adding the log
    this.filterLogs()
  }

  isDevToolsLog(logData) {
    // Check if the message contains DevTools-related keywords
    const message = logData.message || ''
    const devToolsPatterns = [
      /DevTools:/i,
      /DevTools\s+monitoring/i,
      /Capturing\s+(DOM|performance|console)/i,
      /postMessage\s+handler/i,
      /DevTools.*initialized/i,
      /preview-devtools:/i,
      /dom-data-received/i,
      /console-log-received/i,
      /performance-data-received/i
    ]
    
    return devToolsPatterns.some(pattern => pattern.test(message))
  }

  createLogEntry(logData) {
    const div = document.createElement('div')
    div.className = `console-log-entry flex items-start gap-2 px-3 py-1 border-b border-base-300/30 hover:bg-base-200/30 ${this.getLogLevelClass(logData.level)}`
    div.dataset.level = logData.level
    div.dataset.consoleLogTarget = 'logEntry'
    div.dataset.logId = logData.id || `log-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    
    // Store the log data on the element for later use
    div.logData = logData
    
    // Icon
    const icon = document.createElement('i')
    icon.className = `fas fa-${this.getLogLevelIcon(logData.level)} mt-0.5`
    div.appendChild(icon)
    
    // Content container
    const contentDiv = document.createElement('div')
    contentDiv.className = 'flex-1 break-all'
    
    // Source if available
    if (logData.source) {
      const sourceSpan = document.createElement('span')
      sourceSpan.className = 'text-base-content/60 text-xs'
      sourceSpan.textContent = this.formatSource(logData.source)
      contentDiv.appendChild(sourceSpan)
      contentDiv.appendChild(document.createTextNode(' '))
    }
    
    // Message
    const messageSpan = document.createElement('span')
    messageSpan.className = 'whitespace-pre-wrap'
    messageSpan.textContent = this.formatMessage(logData.message)
    contentDiv.appendChild(messageSpan)
    
    div.appendChild(contentDiv)
    
    // Actions container
    const actionsDiv = document.createElement('div')
    actionsDiv.className = 'flex items-center gap-2'
    
    // Add "Fix with Claude" button for errors (show as toast above tabs)
    if (logData.level === 'error' && !this.isDevToolsLog(logData)) {
      // Show error notification toast above tabs
      this.showErrorNotificationToast(logData)
      
      // Still add the inline fix button but hidden by default
      const fixButton = document.createElement('button')
      fixButton.className = 'btn btn-xs btn-ghost text-primary opacity-0 group-hover:opacity-100 transition-opacity'
      fixButton.innerHTML = '<i class="fas fa-robot text-xs"></i> Fix'
      fixButton.title = 'Send this error to Claude Code for analysis'
      fixButton.addEventListener('click', (e) => {
        e.stopPropagation()
        this.sendSingleErrorToClaude(logData)
      })
      actionsDiv.appendChild(fixButton)
      
      // Add group class to parent for hover effect
      div.classList.add('group')
    }
    
    // Timestamp
    const timestampSpan = document.createElement('span')
    timestampSpan.className = 'text-base-content/40 whitespace-nowrap text-xs'
    timestampSpan.textContent = this.formatTimestamp(logData.timestamp)
    actionsDiv.appendChild(timestampSpan)
    
    div.appendChild(actionsDiv)
    
    return div
  }

  getLogLevelClass(level) {
    switch (level?.toLowerCase()) {
      case 'error':
        return 'text-error bg-error/10'
      case 'warn':
      case 'warning':
        return 'text-warning bg-warning/10'
      case 'info':
        return 'text-info bg-info/10'
      case 'debug':
        return 'text-base-content/60'
      default:
        return 'text-base-content'
    }
  }

  getLogLevelIcon(level) {
    switch (level?.toLowerCase()) {
      case 'error':
        return 'times-circle'
      case 'warn':
      case 'warning':
        return 'exclamation-triangle'
      case 'info':
        return 'info-circle'
      case 'debug':
        return 'bug'
      default:
        return 'chevron-right'
    }
  }

  formatMessage(message) {
    if (typeof message === 'string') {
      return message
    } else if (Array.isArray(message)) {
      return message.map(m => this.formatValue(m)).join(' ')
    } else {
      return this.formatValue(message)
    }
  }

  formatValue(value) {
    if (value === null) return 'null'
    if (value === undefined) return 'undefined'
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2)
      } catch {
        return String(value)
      }
    }
    return String(value)
  }

  formatSource(source) {
    if (!source) return ''
    // Extract file and line from stack trace
    const match = source.match(/([^\/\s]+:\d+:\d+)/)
    return match ? match[1] : source
  }

  formatTimestamp(timestamp) {
    if (!timestamp) return ''
    try {
      const date = new Date(timestamp)
      return date.toLocaleTimeString('en-US', { 
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        fractionalSecondDigits: 3
      })
    } catch {
      return ''
    }
  }

  applyFilter(event) {
    const filter = event.currentTarget.dataset.filter
    this.currentFilterValue = filter
    
    // Update button states
    this.filterButtonTargets.forEach(btn => {
      if (btn.dataset.filter === filter) {
        btn.classList.add('btn-active')
      } else {
        btn.classList.remove('btn-active')
      }
    })
    
    this.filterLogs()
  }

  filterLogs() {
    const searchTerm = this.searchInputTarget.value.toLowerCase()
    this.searchTermValue = searchTerm
    
    let visibleCount = 0
    
    this.logEntryTargets.forEach(entry => {
      const level = entry.dataset.level?.toLowerCase()
      const text = entry.textContent.toLowerCase()
      const isDevTools = entry.dataset.devtools === 'true'
      const isAllSpark = text.includes('[allspark]') || text.includes('[AllSpark]')
      
      // Normalize level for comparison
      const normalizedLevel = this.normalizeLogLevel(level)
      const normalizedFilter = this.normalizeLogLevel(this.currentFilterValue)
      
      // Check filter
      let matchesFilter = normalizedFilter === 'all' || normalizedLevel === normalizedFilter
      
      // Check search
      let matchesSearch = !searchTerm || text.includes(searchTerm)
      
      // Check DevTools visibility
      let devToolsVisible = !isDevTools || this.showDevToolsLogsValue
      
      // Check AllSpark visibility (checkbox checked = show, unchecked = hide)
      let allSparkVisible = !isAllSpark || this.showAllSparkLogsValue
      
      // Show/hide entry - use display style instead of hidden class for better control
      if (matchesFilter && matchesSearch && devToolsVisible && allSparkVisible) {
        entry.style.display = ''
        entry.classList.remove('hidden')
        visibleCount++
      } else {
        entry.style.display = 'none'
        entry.classList.add('hidden')
      }
    })
    
    // Update the console badge count to reflect only visible logs
    this.updateConsoleBadgeCount(visibleCount)
  }

  normalizeLogLevel(level) {
    if (!level) return 'log'
    const normalized = level.toLowerCase()
    // Map variations to standard names
    if (normalized === 'warn') return 'warning'
    if (normalized === 'debug') return 'log'
    return normalized
  }

  updateConsoleBadgeCount(count) {
    // Find the preview devtools controller and update its badge
    const devtoolsElement = this.element.closest('[data-controller*="preview-devtools"]')
    if (devtoolsElement) {
      const badge = devtoolsElement.querySelector('[data-preview-devtools-target="consoleBadge"]')
      if (badge) {
        badge.textContent = count > 0 ? count : ''
        badge.classList.toggle('hidden', count === 0)
      }
    }
  }

  clearLogs() {
    this.logsValue = []
    this.errorLogsValue = []
    this.updateErrorCount()
    this.logsContainerTarget.innerHTML = `
      <div class="flex items-center justify-center h-full text-base-content/40">
        <div class="text-center">
          <i class="fas fa-terminal text-2xl mb-2"></i>
          <p>Console output will appear here</p>
        </div>
      </div>
    `
    this.clearStoredLogs()
    this.updateConsoleBadgeCount(0)
  }

  togglePreserveLog(event) {
    this.preserveLogValue = event.target.checked
    if (!this.preserveLogValue) {
      this.clearStoredLogs()
    }
  }

  toggleDevToolsLogs(event) {
    this.showDevToolsLogsValue = event.target.checked
    
    // Re-filter all logs to apply DevTools visibility
    this.filterLogs()
  }

  toggleAllSparkLogs(event) {
    this.showAllSparkLogsValue = event.target.checked
    
    // Re-filter all logs to apply AllSpark visibility
    this.filterLogs()
  }


  exportLogs() {
    const data = {
      logs: this.logsValue,
      exportedAt: new Date().toISOString(),
      filter: this.currentFilterValue,
      searchTerm: this.searchTermValue
    }
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `console-logs-${Date.now()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // Storage methods
  storeLog(logData) {
    try {
      const key = `console-logs-${this.buildSessionIdValue}`
      const stored = localStorage.getItem(key)
      const logs = stored ? JSON.parse(stored) : []
      logs.push(logData)
      // Keep only last 1000 logs
      if (logs.length > 1000) {
        logs.splice(0, logs.length - 1000)
      }
      localStorage.setItem(key, JSON.stringify(logs))
    } catch (e) {
      console.error('Failed to store log:', e)
    }
  }

  loadStoredLogs() {
    if (!this.preserveLogValue) return
    
    try {
      const key = `console-logs-${this.buildSessionIdValue}`
      const stored = localStorage.getItem(key)
      if (stored) {
        const logs = JSON.parse(stored)
        logs.forEach(log => this.addLog(log))
      }
    } catch (e) {
      console.error('Failed to load stored logs:', e)
    }
  }

  clearStoredLogs() {
    try {
      const key = `console-logs-${this.buildSessionIdValue}`
      localStorage.removeItem(key)
    } catch (e) {
      console.error('Failed to clear stored logs:', e)
    }
  }

  // Claude Integration Methods
  updateErrorCount() {
    const errorCount = this.errorLogsValue.length
    
    if (this.hasErrorCountBadgeTarget) {
      this.errorCountBadgeTarget.textContent = errorCount
    }
    
    if (this.hasSendErrorsButtonTarget) {
      if (errorCount > 0) {
        this.sendErrorsButtonTarget.classList.remove('hidden')
        this.sendErrorsButtonTarget.querySelector('span').lastChild.textContent = 
          errorCount === 1 ? ' Error' : ' Errors'
      } else {
        this.sendErrorsButtonTarget.classList.add('hidden')
      }
    }
  }

  async sendSingleErrorToClaude(errorData) {
    const context = await this.collectDebugContext([errorData])
    this.sendToClaudeChat(context)
  }

  async sendErrorsToClaude() {
    if (this.errorLogsValue.length === 0) return
    
    const context = await this.collectDebugContext(this.errorLogsValue)
    this.sendToClaudeChat(context)
  }

  async collectDebugContext(errors) {
    const context = {
      errors: errors,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      buildSessionId: this.buildSessionIdValue
    }
    
    // Try to get DOM context from the DOM inspector if available
    try {
      const domInspector = document.querySelector('[data-controller="dom-inspector"]')
      if (domInspector && domInspector._stimulus) {
        const domController = this.application.getControllerForElementAndIdentifier(domInspector, 'dom-inspector')
        if (domController && domController.currentDomData) {
          context.domSnapshot = domController.currentDomData
        }
      }
    } catch (e) {
      console.debug('Could not get DOM snapshot:', e)
    }
    
    // Get recent console logs for context (last 20 non-error logs)
    const recentLogs = this.logsValue
      .filter(log => log.level !== 'error')
      .slice(-20)
    
    if (recentLogs.length > 0) {
      context.recentLogs = recentLogs
    }
    
    // Get the iframe URL if available
    try {
      const iframe = document.querySelector('#preview-iframe')
      if (iframe) {
        context.iframeUrl = iframe.src
      }
    } catch (e) {
      console.debug('Could not get iframe URL:', e)
    }
    
    return context
  }

  sendToClaudeChat(context) {
    // Format the message for Claude
    const errorSummary = context.errors.map(e => `- ${e.message}`).join('\n')
    const message = `I'm encountering errors in my application that I need help fixing:

**Errors Found:**
${errorSummary}

**Context:**
- URL: ${context.iframeUrl || context.url}
- Timestamp: ${context.timestamp}
- Build Session: ${context.buildSessionId}

${context.errors[0].stack ? `**Stack Trace:**\n\`\`\`\n${context.errors[0].stack}\n\`\`\`` : ''}

${context.recentLogs ? `**Recent Console Activity:**\n${context.recentLogs.slice(-5).map(log => `[${log.level}] ${log.message}`).join('\n')}` : ''}

Please help me understand and fix these errors.`

    // Find the Claude Code chat component and send the message
    const claudeChat = document.querySelector('[data-controller="claude-code-chat"]')
    if (claudeChat) {
      const chatController = this.application.getControllerForElementAndIdentifier(claudeChat, 'claude-code-chat')
      if (chatController) {
        // Set the message in the input field
        if (chatController.hasMessageInputTarget) {
          chatController.messageInputTarget.value = message
          // Trigger input event to update the controller's state
          chatController.messageInputTarget.dispatchEvent(new Event('input'))
          
          // Focus the input to draw attention
          chatController.messageInputTarget.focus()
          
          // Optionally auto-send (commented out to let user review first)
          // chatController.sendMessage()
        }
      } else {
        console.warn('Claude Code chat controller not found')
        // Fallback: Copy to clipboard
        this.copyErrorContextToClipboard(message)
      }
    } else {
      console.warn('Claude Code chat component not found')
      // Fallback: Copy to clipboard
      this.copyErrorContextToClipboard(message)
    }
  }

  copyErrorContextToClipboard(message) {
    navigator.clipboard.writeText(message).then(() => {
      // Show toast notification
      const toast = document.createElement('div')
      toast.className = 'toast toast-top toast-end'
      toast.innerHTML = `
        <div class="alert alert-info">
          <i class="fas fa-copy mr-2"></i>
          <span>Error context copied to clipboard. Paste it in Claude Code chat.</span>
        </div>
      `
      document.body.appendChild(toast)
      setTimeout(() => toast.remove(), 3000)
    }).catch(err => {
      console.error('Failed to copy to clipboard:', err)
    })
  }

  showErrorNotificationToast(errorData) {
    // Find the tabs container to position the toast above it
    const tabsContainer = document.querySelector('.tabs-boxed')
    if (!tabsContainer) return
    
    // Create unique ID for this error notification
    const errorId = `error-toast-${Date.now()}`
    
    // Check if we already have a notification for similar error
    const existingToast = document.querySelector('.error-notification-toast')
    if (existingToast) {
      // Update counter if similar error
      const counter = existingToast.querySelector('.error-counter')
      if (counter) {
        const count = parseInt(counter.textContent) || 1
        counter.textContent = count + 1
      }
      return
    }
    
    // Create toast notification
    const toast = document.createElement('div')
    toast.id = errorId
    toast.className = 'error-notification-toast alert alert-warning mb-4 flex items-center justify-between shadow-lg'
    toast.style.cssText = 'position: relative; z-index: 50;'
    toast.innerHTML = `
      <div class="flex items-center gap-2 flex-1">
        <i class="fas fa-exclamation-triangle"></i>
        <div class="flex-1">
          <span class="font-medium">JavaScript Error Detected</span>
          <span class="text-sm ml-2 opacity-90">${this.truncateMessage(errorData.message, 60)}</span>
          <span class="badge badge-sm badge-warning ml-2 error-counter">1</span>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <button class="btn btn-sm btn-ghost" onclick="this.parentElement.parentElement.parentElement.remove()">
          <i class="fas fa-times"></i>
        </button>
        <button class="btn btn-sm btn-primary" id="${errorId}-fix">
          <i class="fas fa-robot mr-1"></i>
          Fix with Claude
        </button>
      </div>
    `
    
    // Insert above tabs
    const previewContent = document.getElementById('preview-content')
    if (previewContent) {
      previewContent.insertBefore(toast, previewContent.firstChild)
    } else {
      // Fallback: insert at top of console container
      this.element.insertBefore(toast, this.element.firstChild)
    }
    
    // Add click handler for fix button
    const fixButton = document.getElementById(`${errorId}-fix`)
    if (fixButton) {
      fixButton.addEventListener('click', () => {
        this.sendSingleErrorToClaude(errorData)
        toast.remove()
      })
    }
    
    // Auto-remove after 10 seconds
    setTimeout(() => {
      if (document.getElementById(errorId)) {
        toast.remove()
      }
    }, 10000)
  }

  truncateMessage(message, maxLength) {
    if (!message) return ''
    if (message.length <= maxLength) return message
    return message.substring(0, maxLength) + '...'
  }
}